import React, { useCallback } from 'react';
import { EdgeProps, getSmoothStepPath } from 'reactflow';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { actionsSelector } from '~/components/page/Automation/v2/state/actions';
import AnimatedPath from '../AnimatedPath';
import DroppableArea from '../DroppableArea';
import AddBlockButton from '../AddBlockButton';
import useErrorReporter from '~/hooks/useErrorReporter';

import { getActionTemplate } from '../../../../constants/actionTemplates';
import useBuilderContext from '../../../../hooks/useBuilderContext';
import parseEventData from '../../utils/parseEventData';
import onUpdateActions from '../../utils/onUpdateActions';
import { ClientFlowAction } from '~/graphql/types.client';
import { shouldDisable } from '~/components/page/Automation/v2/state/interactions';
import { EDGE_BORDER_RADIUS } from '../BaseEdge';
import { MARKER_END_DISABLED } from '../../../MarkerDefinitions';
import usePasteAction from '~/components/page/Automation/v2/components/Builder/hooks/usePasteAction';
import InsertCopiedActionContainer from '../InsertCopiedActionContainer';

export type EdgeData = {
  action: ClientFlowAction;
  parentNode: ClientFlowAction;
  id: string;
  parentId: string;
};

export type Props = EdgeProps<EdgeData> & {};

const DropEdge: React.FCC<Props> = React.memo(
  ({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style = {},
    data,
    source,
    target,
    markerEnd,
  }) => {
    const { flowBlueprintId, accountId } = useBuilderContext();
    const reporter = useErrorReporter();
    const edgePath = getSmoothStepPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
      borderRadius: EDGE_BORDER_RADIUS,
    });

    const setActions = useSetRecoilState(
      actionsSelector({
        accountId,
        flowBlueprintId,
      }),
    );

    const onDrop = useCallback(
      (event: React.DragEvent<any>) => {
        const dropData = parseEventData(
          event.dataTransfer.getData('application/reactflow'),
          reporter,
        );

        // When dropData.id is equal to data.id we dropped it on it's own edge so do nothing
        if (dropData !== null && data != null && dropData.id !== data.id) {
          setActions(actions =>
            onUpdateActions(actions, {
              source,
              subject: dropData,
              target,
            }),
          );
        }
      },
      [data, reporter, setActions, source, target],
    );

    const onAddAction = useCallback(
      (actionType: ClientFlowAction['actionType']) => {
        const actionTemplate = getActionTemplate({
          flowBlueprintId,
          accountId,
          actionType,
        });

        if (actionTemplate !== null && data != null) {
          setActions(actions =>
            onUpdateActions(actions, {
              source,
              target,
              subject: actionTemplate,
            }),
          );
        }
      },
      [accountId, data, flowBlueprintId, source, target, setActions],
    );

    const { onInsertAction } = usePasteAction({ source, target });

    const disabled = useRecoilValue(shouldDisable);

    return (
      <>
        <AnimatedPath
          id={id}
          style={{
            ...style,
            strokeWidth: 2,
            pointerEvents: 'none',
            opacity: disabled ? 0.5 : 1,
            transition: 'opacity 0.5s ease-out',
          }}
          className="react-flow__edge-path"
          d={edgePath[0]}
          markerEnd={disabled ? MARKER_END_DISABLED : markerEnd}
        />
        <DroppableArea
          sourceX={sourceX}
          sourceY={sourceY}
          id={id}
          onDrop={onDrop}
        />

        <InsertCopiedActionContainer
          onInsertAction={onInsertAction}
          sourceX={sourceX}
          sourceY={sourceY}
          targetY={targetY}
          dataTestId={`insertActionContainer-${source}`}
        />

        {!disabled && (
          <AddBlockButton
            id={id}
            targetX={targetX}
            targetY={targetY}
            onAddAction={onAddAction}
            parentActionType={data?.parentNode?.actionType}
          />
        )}
      </>
    );
  },
);

export default DropEdge;
